// Core
import { createApp, h } from 'vue'
import {
  createInertiaApp,
  Link as InertiaLink,
  router,
  usePage,
} from '@inertiajs/vue3'
import Layout from '../layouts/Layout.vue'

// Styles
import 'vue-multiselect/dist/vue-multiselect.css'
import 'swiper/swiper.min.css'
import 'floating-vue/dist/style.css'
import '@/assets/styles/tailwind.scss'
import '@/assets/styles/fonts.scss'
import '@/assets/styles/transitions.scss'
import '@/assets/styles/global.scss'
import '@/assets/styles/animations.scss'
import '@/assets/styles/buttons.scss'

// Libraries
import FloatingVue, { hideAllPoppers } from 'floating-vue'
import axios from 'axios'
import { VueRecaptchaPlugin } from 'vue-recaptcha/head'
import Notifications from '@kyvg/vue3-notification'
import InfiniteLoading from 'vue-infinite-loading'
import ActionCableVue from 'actioncable-vue'
import { createPinia } from 'pinia'
import { Settings } from 'luxon'

// Utils
import { isTouchDevice } from '@/shared/utils/isTouchDevice.js'

// AppSignal
import { appsignal } from './appsignal'
import snakeCaseKeys from 'snakecase-keys'

axios.defaults.headers.common['X-CSRF-TOKEN'] =
  document.head.querySelector('[name=csrf-token]').content
axios.defaults.headers.common['Accept'] = 'application/json'

FloatingVue.options.themes.menu.popperTriggers = ['hover']

// Closing popover on escape key press and on Inertia page change
document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape') return
  hideAllPoppers()
})
router.on('success', hideAllPoppers)

Settings.defaultLocale = 'en'

// Init app
createInertiaApp({
  progress: {
    // primary-500 (tailwind.config.js)
    color: '#006CFF',
  },
  resolve: (name) => {
    let pages = import.meta.glob('../modules/**/*.page.vue')
    const importPage = pages[`../modules/${name}.page.vue`]
    return importPage().then((module) => {
      module.default.layout = module.default.layout || Layout

      // Body page color class has priority over layout color class
      const bodyClassMeta =
        module.default?.meta?.bodyClass ||
        module.default.layout?.meta?.bodyClass

      document.body.classList.value = bodyClassMeta || 'tw-bg-neutral-50'

      return module.default
    })
  },
  setup({ el, App, props, plugin }) {
    const pinia = createPinia()
    const app = createApp({ render: () => h(App, props) })
      .component('InertiaLink', InertiaLink)
      .use(pinia)
      .use(FloatingVue, {
        themes: {
          tooltip: {
            triggers: isTouchDevice() ? ['click'] : ['hover', 'focus'],
            html: false,
          },
          dropdown: {
            html: false,
          },
        },
      })
      .use(VueRecaptchaPlugin, {
        v2SiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
      })
      .use(Notifications)
      .component('InfiniteLoading', {
        setup(props, { slots }) {
          return () =>
            h(InfiniteLoading, props, {
              spinner: () => slots.spinner(),
              'no-results': () =>
                slots['no-results'] ? slots['no-results']() : '',
              'no-more': () => (slots['no-more'] ? slots['no-more']() : ''),
              error: () => 'Something went wrong while loading data.',
            })
        },
      })
      .use(ActionCableVue, {
        debug: true,
        debugLevel: 'error',
        connectionUrl: import.meta.env.VITE_ACTION_CABLE_URL, // or function which returns a string with your JWT appended to your server URL as a query parameter
        connectImmediately: true,
      })
      .use(plugin)

    app.config.errorHandler = (error, vm, info) => {
      const userId = usePage().props?.currentUser?.id || 'GUEST'
      const currentPage = window.location.href
      const componentName = vm.$vnode
        ? vm.$vnode.componentOptions.tag
        : vm.$options.name || vm.$options.__name
      const span = appsignal.createSpan()
      span
        .setAction(componentName || '[unknown Vue component]')
        .setTags(
          snakeCaseKeys({
            framework: 'Vue',
            info: info,
            version: app.version,
            userId,
            currentPage,
          }),
        )
        .setError(error)
      appsignal.send(span)
      if (
        typeof console !== 'undefined' &&
        typeof console.error === 'function'
      ) {
        console.error(error)
      }
    }

    app.mount(el)
  },
}).then(() => {})
